
import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Plus } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { use24hScheduling } from "@/hooks/use24hScheduling";
import { toast } from 'sonner';
import { TimeBlockModalProps } from './types';

const TimeBlockModal: React.FC<TimeBlockModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  psychologistId,
  onSuccess
}) => {
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  
  const { generateTimeOptions, saveTimeBlock } = use24hScheduling();
  const timeOptions = generateTimeOptions();

  const handleSaveBlock = useCallback(async () => {
    if (!psychologistId || !selectedDate || !startTime || !endTime) {
      toast.error('Preencha todos os campos');
      return;
    }

    if (startTime >= endTime) {
      toast.error('Horário de início deve ser anterior ao horário de fim');
      return;
    }

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const success = await saveTimeBlock(psychologistId, dateStr, startTime, endTime);
    
    if (success) {
      onClose();
      setStartTime('');
      setEndTime('');
      toast.success('Horário configurado com sucesso!');
      onSuccess();
    }
  }, [psychologistId, selectedDate, startTime, endTime, saveTimeBlock, onClose, onSuccess]);

  const handleClose = useCallback(() => {
    onClose();
    setStartTime('');
    setEndTime('');
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Configurar Horário
          </DialogTitle>
          <DialogDescription>
            {selectedDate && `Data: ${format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Horário Início */}
          <div className="space-y-2">
            <Label htmlFor="start-time">Horário de Início</Label>
            <Select value={startTime} onValueChange={setStartTime}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o horário de início" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {timeOptions.map((time) => (
                  <SelectItem key={time} value={time}>
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Horário Fim */}
          <div className="space-y-2">
            <Label htmlFor="end-time">Horário de Fim</Label>
            <Select value={endTime} onValueChange={setEndTime}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o horário de fim" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {timeOptions.map((time) => (
                  <SelectItem key={time} value={time}>
                    {time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preview do bloco */}
          {startTime && endTime && (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900">
                Bloco: {startTime} - {endTime}
              </p>
              <p className="text-xs text-blue-700">
                Slots de 30min serão gerados automaticamente
              </p>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleSaveBlock} className="flex-1">
              <Plus className="h-4 w-4 mr-2" />
              Salvar Horário
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimeBlockModal;
